import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { getFileUrlFromS3 } from 'app/helpers/awsUtils';
import { AppTopNav } from 'app/modules/shared/AppTopNav';

// Helper to parse query string
function useQuery() {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
}

import { ResumeService } from 'app/services/resumeService';

const ViewResume: React.FC = () => {
    const query = useQuery();
    const resumeKey = query.get('resumeKey');
    const [resumeUrl, setResumeUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const resumeService = new ResumeService();

    useEffect(() => {
        const fetchResume = async () => {
            if (!resumeKey) {
                setError('No resume key provided.');
                setLoading(false);
                return;
            }

            try {
                console.log('Processing resume key:', resumeKey);

                let targetKey = resumeKey;
                const isPdf = resumeKey.toLowerCase().endsWith('.pdf');

                if (!isPdf) {
                    console.log('File is not a PDF, attempting conversion...');
                    // remove extension from original key to guess the pdf key name? 
                    // In CandidateProfile logic: fileNameWithoutExtension + '.pdf'
                    // format: attached_resumes/69_resume.txt -> attached_resumes/69_resume.pdf
                    const fileNameWithoutExtension = resumeKey.replace(/\.[^/.]+$/, '');
                    targetKey = fileNameWithoutExtension + '.pdf';

                    try {
                        // Trigger conversion
                        // Note: CandidateProfile passes 'public/' prefix to convertDocument if it's not present?
                        // The key usually starts with 'attached_resumes/...'
                        // Let's try passing it as is, or prepending public/ if needed. 
                        // CandidateProfileForInterview.tsx line 63: await resumeService.convertDocument('public/' + s3Key);
                        // We should probably do the same.
                        await resumeService.convertDocument('public/' + resumeKey);
                        console.log('Conversion request sent.');
                    } catch (convErr) {
                        console.warn('Conversion failed or already converted:', convErr);
                        // Continue to try fetching the .pdf version anyway
                    }
                }

                console.log('Fetching URL for key:', targetKey);
                const result = await getFileUrlFromS3(targetKey);
                console.log('S3 Result:', result);

                if (result && result.fileUrl) {
                    setResumeUrl(result.fileUrl);
                } else {
                    setError('Could not retrieve file URL.');
                }
            } catch (err) {
                console.error('Error fetching resume:', err);
                setError(`Failed to load resume: ${err instanceof Error ? err.message : String(err)}`);
            } finally {
                setLoading(false);
            }
        };

        fetchResume();
    }, [resumeKey]);

    return (
        <div className="d-flex flex-column h-100">
            <div className="container-fluid flex-grow-1 p-0" style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
                {loading && (
                    <div className="d-flex justify-content-center align-items-center h-100">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        {!resumeKey?.toLowerCase().endsWith('.pdf') && <div className="ms-2">Converting document...</div>}
                    </div>
                )}

                {error && (
                    <div className="d-flex justify-content-center align-items-center h-100 flex-column">
                        <div className="alert alert-danger">{error}</div>
                        <p className="text-muted">Attempted Key: {resumeKey}</p>
                    </div>
                )}

                {!loading && !error && resumeUrl && (
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                        <Worker workerUrl="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.6.347/pdf.worker.min.js">
                            <div style={{ height: '100%' }}>
                                <Viewer
                                    fileUrl={resumeUrl}
                                />
                            </div>
                        </Worker>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ViewResume;
